import type { StitchKind } from "../tools/StitchTool";
import type { ConstructionKind } from "../tools/ConstructionTool";
import type {
  StitchLine,
  StitchPitchMode,
  StitchShape,
} from "../model/Project";
import {
  STITCH_PITCH_PRESETS_MM,
  STITCH_PITCH_PRESETS_SPI,
  stitchSizeRange,
} from "../model/stitching";
import { OFFSET_TOOL_ICON_SVG } from "../icons/offsetTool";
import chiselDiamondRaw from "../icons/chisel-diamond.svg?raw";
import chiselRoundRaw from "../icons/chisel-round.svg?raw";
import chiselSlantRaw from "../icons/chisel-slant.svg?raw";
import shapeCircleRaw from "../icons/shape-circle.svg?raw";
import shapeEllipseRaw from "../icons/shape-ellipse.svg?raw";
import shapeFreehandRaw from "../icons/shape-freehand.svg?raw";
import shapeLineRaw from "../icons/shape-line.svg?raw";
import shapePolygonRaw from "../icons/shape-polygon.svg?raw";
import shapePolylineRaw from "../icons/shape-polyline.svg?raw";
import shapeRectRaw from "../icons/shape-rect.svg?raw";
import shapeRegularPolygonRaw from "../icons/shape-regular-polygon.svg?raw";
import shapeSlotRaw from "../icons/shape-slot.svg?raw";
import shapeSquareRaw from "../icons/shape-square.svg?raw";
import standardSizeRaw from "../icons/standard-size.svg?raw";
import { stripIconSvg } from "../icons/stripIconSvg";
import { searchStandardSizes, type StandardSize } from "../model/standardSizes";
import { makeUnitCell, mmInchInputs } from "./mmInchInputs";
import { parseLengthInput } from "./lengthParse";
import { getShowSecondaryUnits, getUnits } from "../preferences";
import { cssVar } from "../colors";
// Side-effect import registers the <range-group> custom element; the type is
// imported separately so the registration isn't tree-shaken as type-only.
import "./rangeGroup";
import type { RangeGroupElement } from "./rangeGroup";
import { wireLengthFieldEnter } from "./lengthFieldEnter";

/** SVG icons for the shape gallery (shared between the Cutout and leather
 *  Shape tools) — drawn at 24×24 with currentColor so they pick up the
 *  panel's text color and accent on selection.
 *
 *  The glyphs live in `src/icons/shape-*.svg` and come through the shared
 *  {@link stripIconSvg} (#216), the same normaliser the static `<!-- svg:… -->`
 *  markers in index.html use. They stay runtime constants rather than markers
 *  because the gallery's tiles are built with `document.createElement` — there
 *  is no markup to carry one. */
const SHAPE_ICONS: Record<
  "rect" | "ellipse" | "freehand" | "polygon" | "slot",
  { label: string; svg: string }
> = {
  rect: { label: "Rectangle", svg: stripIconSvg(shapeRectRaw) },
  ellipse: { label: "Ellipse", svg: stripIconSvg(shapeEllipseRaw) },
  slot: { label: "Oblong", svg: stripIconSvg(shapeSlotRaw) },
  freehand: { label: "Freehand", svg: stripIconSvg(shapeFreehandRaw) },
  polygon: { label: "Polygon", svg: stripIconSvg(shapePolygonRaw) },
};

/** Polyline icon — open zigzag (no closing segment). Sits in the
 *  construction gallery as a sibling of `polygon` (which closes). */
const ICON_POLYLINE = stripIconSvg(shapePolylineRaw);

/** Line icon — single straight diagonal segment. Sits in the
 *  construction gallery for the parametric single-line primitive
 *  (issue #118). */
const ICON_LINE = stripIconSvg(shapeLineRaw);

/** 1:1 versions used for the rect/ellipse tiles while snap is on, so the
 *  icon matches what the tool will actually draw (square / circle). */
const ICON_SQUARE = stripIconSvg(shapeSquareRaw);
const ICON_CIRCLE = stripIconSvg(shapeCircleRaw);

/** The Standard-sizes tile (#32): a card-shaped rectangle with a ruler tick
 *  along its edge — "a shape whose size is already known". */
const STANDARD_SIZE_ICON = stripIconSvg(standardSizeRaw);

/** Regular-hexagon version of the polygon tile, shown while the regular
 *  drag-out mode is active (size snap / Equilateral, #928) — mirrors the
 *  rect→square / ellipse→circle swap. */
const ICON_REGULAR_POLYGON = stripIconSvg(shapeRegularPolygonRaw);

/** One tile in a shape / seam / construction kind gallery. All four galleries
 *  build structurally identical buttons, so the markup lives here once. */
function galleryTile(
  label: string,
  svg: string,
  selected: boolean,
  onClick: () => void,
  /** When set, the tile is greyed out and unclickable, and the string is shown
   *  as its tooltip explaining why. */
  disabledReason?: string | null,
): HTMLButtonElement {
  const tile = document.createElement("button");
  tile.type = "button";
  tile.className = "shape-tile";
  if (selected) tile.classList.add("selected");
  tile.title = disabledReason || label;
  tile.setAttribute("aria-label", label);
  tile.innerHTML = svg;
  if (disabledReason) {
    tile.disabled = true;
  } else {
    tile.addEventListener("click", onClick);
  }
  return tile;
}

type GalleryKind =
  | "offset"
  | "rect"
  | "ellipse"
  | "freehand"
  | "polygon"
  | "slot"
  /** Not a shape of its own (#32): picking it swaps the panel for a searchable
   *  list of real-world objects, and choosing one draws the rectangle or
   *  ellipse that object is. Last in the row because it is a way of ARRIVING at
   *  a shape rather than another shape to draw. */
  | "standard";

export function shapeGallery(
  selected: GalleryKind,
  onSelect: (v: GalleryKind) => void,
  snapOn = false,
  // Cutout default reads "Oblong"; the leather Shape panel passes "Pill".
  // Internal kind id stays "slot" — only the visible label changes.
  slotLabel = "Oblong",
  // Regular-polygon drag-out active (#928): the polygon tile flips to a
  // regular hexagon icon + label, like rect→square.
  polygonRegularOn = false,
  // Why the Offset tile is unavailable, or null when it is (#972). Offset
  // derives a shape from an outline that already exists, so with nothing to
  // offset from the tile is greyed rather than silently doing nothing.
  offsetDisabledReason: string | null = null,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = "Shape";
  wrap.appendChild(lbl);

  const grid = document.createElement("div");
  grid.className = "shape-gallery";
  // Structured primitives first; then the loose freeform tool; Offset last.
  // Offset isn't a shape you draw — it derives one from an outline that already
  // exists — so it sits apart from the draw-a-new-shape tiles rather than
  // leading them (#972).
  const order: Array<GalleryKind> = [
    "rect",
    "ellipse",
    "slot",
    "polygon",
    "freehand",
    "offset",
    "standard",
  ];
  for (const key of order) {
    if (key === "standard") {
      grid.appendChild(
        galleryTile("Standard", STANDARD_SIZE_ICON, key === selected, () =>
          onSelect(key),
        ),
      );
      continue;
    }
    if (key === "offset") {
      grid.appendChild(
        galleryTile(
          "Offset",
          OFFSET_TOOL_ICON_SVG,
          key === selected,
          () => onSelect(key),
          offsetDisabledReason,
        ),
      );
      continue;
    }
    const meta = SHAPE_ICONS[key];
    // While snap is on the rect/ellipse drag is constrained to 1:1, so the
    // tile shows what will actually be drawn — square / circle (label + icon).
    const label =
      snapOn && key === "rect"
        ? "Square"
        : snapOn && key === "ellipse"
          ? "Circle"
          : key === "slot"
            ? slotLabel
            : polygonRegularOn && key === "polygon"
              ? "Regular polygon"
              : meta.label;
    const svg =
      snapOn && key === "rect"
        ? ICON_SQUARE
        : snapOn && key === "ellipse"
          ? ICON_CIRCLE
          : polygonRegularOn && key === "polygon"
            ? ICON_REGULAR_POLYGON
            : meta.svg;
    grid.appendChild(
      galleryTile(label, svg, key === selected, () => onSelect(key)),
    );
  }
  wrap.appendChild(grid);
  return wrap;
}

/** Construction-tool gallery. Same five primitives as the shape gallery
 *  plus an open-ended `polyline` tile for single guide lines. Polyline
 *  leads since open-ended lines are the most common construction case. */
/** Seam-kind gallery for the Stitch tool (#766). Same kinds + order as the
 *  construction gallery (structured shapes first, then polygon / line /
 *  polyline) so the drawing tools stay consistent. `snapOn` swaps rect /
 *  ellipse to their square / circle 1:1 forms. Distinct from
 *  `stitchShapeGallery`, which picks the chisel profile. */
export function stitchKindGallery(
  selected: StitchKind,
  onSelect: (v: StitchKind) => void,
  snapOn = false,
  polygonRegularOn = false,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = "Seam shape";
  wrap.appendChild(lbl);

  const grid = document.createElement("div");
  grid.className = "shape-gallery";
  type Tile = { key: StitchKind; label: string; svg: string };
  const tiles: Tile[] = [
    {
      key: "rect",
      label: snapOn ? "Square" : "Rectangle",
      svg: snapOn ? ICON_SQUARE : SHAPE_ICONS.rect.svg,
    },
    {
      key: "ellipse",
      label: snapOn ? "Circle" : "Ellipse",
      svg: snapOn ? ICON_CIRCLE : SHAPE_ICONS.ellipse.svg,
    },
    { key: "slot", label: "Slot", svg: SHAPE_ICONS.slot.svg },
    {
      key: "polygon",
      label: polygonRegularOn ? "Regular polygon" : "Polygon",
      svg: polygonRegularOn ? ICON_REGULAR_POLYGON : SHAPE_ICONS.polygon.svg,
    },
    { key: "line", label: "Line", svg: ICON_LINE },
    { key: "polyline", label: "Polyline", svg: ICON_POLYLINE },
    // Freehand (#997) sits with the other draw-a-new-seam kinds, in the same
    // slot the leather / cutout galleries give it — after the structured
    // primitives, before Offset.
    { key: "freehand", label: "Freehand", svg: SHAPE_ICONS.freehand.svg },
    // Offset last, matching the leather / cutout galleries (#972): it derives a
    // seam from an existing edge rather than drawing a new shape. Still the
    // DEFAULT selection for this tool (#768) — position and default are
    // independent, and the user's own pick is remembered from then on.
    { key: "offset", label: "Offset", svg: OFFSET_TOOL_ICON_SVG },
  ];
  for (const t of tiles) {
    grid.appendChild(
      galleryTile(t.label, t.svg, t.key === selected, () => onSelect(t.key)),
    );
  }
  wrap.appendChild(grid);
  return wrap;
}

export function constructionGallery(
  selected: ConstructionKind | "standard",
  onSelect: (v: ConstructionKind | "standard") => void,
  snapOn = false,
  polygonRegularOn = false,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = "Shape";
  wrap.appendChild(lbl);

  const grid = document.createElement("div");
  grid.className = "shape-gallery";
  type Tile = {
    key: ConstructionKind | "standard";
    label: string;
    svg: string;
  };
  // Order rationale: structured primitives first (rect → ellipse →
  // slot → polygon), then the loose freeform options last. Polyline
  // sits second-to-last so the always-loosest tool (freehand) keeps
  // the trailing slot.
  const tiles: Tile[] = [
    {
      key: "rect",
      label: snapOn ? "Square" : "Rectangle",
      svg: snapOn ? ICON_SQUARE : SHAPE_ICONS.rect.svg,
    },
    {
      key: "ellipse",
      label: snapOn ? "Circle" : "Ellipse",
      svg: snapOn ? ICON_CIRCLE : SHAPE_ICONS.ellipse.svg,
    },
    { key: "slot", label: "Oblong", svg: SHAPE_ICONS.slot.svg },
    {
      key: "polygon",
      label: polygonRegularOn ? "Regular polygon" : "Polygon",
      svg: polygonRegularOn ? ICON_REGULAR_POLYGON : SHAPE_ICONS.polygon.svg,
    },
    { key: "line", label: "Line", svg: ICON_LINE },
    { key: "polyline", label: "Polyline", svg: ICON_POLYLINE },
    { key: "freehand", label: "Freehand", svg: SHAPE_ICONS.freehand.svg },
    // Last: a way of ARRIVING at a shape, not another shape to draw (#32).
    { key: "standard", label: "Standard", svg: STANDARD_SIZE_ICON },
  ];
  for (const t of tiles) {
    grid.appendChild(
      galleryTile(t.label, t.svg, t.key === selected, () => onSelect(t.key)),
    );
  }
  wrap.appendChild(grid);
  return wrap;
}

/** Common metric slot widths (short axis) in millimetres. */
const SLOT_PRESETS_MM = [3, 4, 5, 6, 8, 10, 12];
/** Common imperial slot widths (label → width in mm). */
const SLOT_PRESETS_INCH: Array<{ label: string; value: number }> = [
  { label: '1/8"', value: 3.175 },
  { label: '3/16"', value: 4.7625 },
  { label: '1/4"', value: 6.35 },
  { label: '5/16"', value: 7.9375 },
  { label: '3/8"', value: 9.525 },
  { label: '1/2"', value: 12.7 },
];

export function slotWidthField(
  width: number,
  onChange: (newWidth: number) => void,
  // Cutout default reads "Oblong"; the leather Shape panel passes "Pill".
  // Internal width state is unit-agnostic — only the label changes per host.
  label = "Oblong width (mm) — drag to set length",
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";

  const lbl = document.createElement("label");
  lbl.textContent = label;
  wrap.appendChild(lbl);

  const matches = (a: number, b: number) => Math.abs(a - b) < 0.005;

  const presetRow = (
    title: string,
    items: Array<{ label: string; value: number }>,
  ) => {
    const row = document.createElement("div");
    row.className = "preset-row";
    const head = document.createElement("span");
    head.className = "preset-row-label";
    head.textContent = title;
    row.appendChild(head);
    for (const item of items) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "preset-chip";
      btn.textContent = item.label;
      if (matches(width, item.value)) btn.classList.add("active");
      btn.title = `${item.value.toFixed(4).replace(/\.?0+$/, "")} mm`;
      btn.addEventListener("click", () => onChange(item.value));
      row.appendChild(btn);
    }
    return row;
  };

  wrap.appendChild(
    presetRow(
      "mm",
      SLOT_PRESETS_MM.map((v) => ({ label: String(v), value: v })),
    ),
  );
  wrap.appendChild(presetRow("in", SLOT_PRESETS_INCH));

  // Numeric mm/inch pair only — chips above already cover the
  // common values, and the range slider was dropped because no
  // caller wants it (every consumer was opting out via `noSlider`).
  const { row: pair } = mmInchInputs(width, onChange, {
    minMm: 1,
    maxMm: 30,
    mmStep: 0.1,
  });
  wrap.appendChild(pair);

  return wrap;
}

/** Common metric leather hole-punch sizes in millimetres (diameter). */
const HOLE_PRESETS_MM = [1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
/** Common imperial leather hole-punch sizes (label → diameter in mm). */
const HOLE_PRESETS_INCH: Array<{ label: string; value: number }> = [
  { label: '1/16"', value: 1.5875 },
  { label: '3/32"', value: 2.38125 },
  { label: '1/8"', value: 3.175 },
  { label: '5/32"', value: 3.96875 },
  { label: '3/16"', value: 4.7625 },
  { label: '7/32"', value: 5.55625 },
  { label: '1/4"', value: 6.35 },
];

/** Build a chip row of corner-radius presets. Standalone (rather than
 *  reusing the inline `presetRow` helpers buried inside hardware /
 *  slot fields) so the Round sub-mode panel can compose chips +
 *  numeric input without dragging in those callers' state. */
export function cornerRadiusPresetRow(
  title: string,
  items: ReadonlyArray<number | { label: string; value: number }>,
  onChange: (mm: number) => void,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "preset-row";
  const head = document.createElement("span");
  head.className = "preset-row-label";
  head.textContent = title;
  row.appendChild(head);
  for (const item of items) {
    const value = typeof item === "number" ? item : item.value;
    const label = typeof item === "number" ? String(item) : item.label;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "preset-chip";
    btn.textContent = label;
    btn.title = `${value.toFixed(4).replace(/\.?0+$/, "")} mm`;
    btn.addEventListener("click", () => onChange(value));
    row.appendChild(btn);
  }
  return row;
}

export function shapeLabel(shape: StitchShape): string {
  return shape === "round"
    ? "Round"
    : shape === "diamond"
      ? "Diamond"
      : "Slant";
}

/** One-line description of a stitch line for the "follows defaults" view.
 *  Picks up the right label for the size dimension so a slant chisel reads
 *  "tooth" rather than "hole". */
export function stitchSummary(line: StitchLine): string {
  const sr = stitchSizeRange(line.shape);
  const sizeNoun = line.shape === "slant" ? "tooth" : "hole";
  return (
    `${shapeLabel(line.shape)} chisel · ` +
    `${line.pitch.toFixed(2)} mm pitch · ` +
    `${line.size.toFixed(2)} mm ${sizeNoun}` +
    (line.shape === "slant"
      ? ` · ${line.slantAngle < 0 ? "regular" : "inverse"} 45°`
      : "") +
    // Reference the field label so the user can map the summary number back
    // to the override panel without surprise.
    ` (${sr.label.toLowerCase()})`
  );
}

/** SVG icons for the stitch chisel-shape gallery — 24×24 with currentColor,
 *  read from `src/icons/chisel-*.svg` through {@link stripIconSvg} (#216). */
const STITCH_SHAPE_ICONS: Record<StitchShape, { label: string; svg: string }> =
  {
    round: {
      label: "Round (awl / pinhole)",
      svg: stripIconSvg(chiselRoundRaw),
    },
    slant: {
      label: "French / slanted slot",
      svg: stripIconSvg(chiselSlantRaw),
    },
    diamond: { label: "Diamond chisel", svg: stripIconSvg(chiselDiamondRaw) },
  };

export function stitchShapeGallery(
  selected: StitchShape,
  onSelect: (v: StitchShape) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = "Chisel";
  wrap.appendChild(lbl);

  const grid = document.createElement("div");
  grid.className = "shape-gallery";
  const order: StitchShape[] = ["round", "slant", "diamond"];
  for (const key of order) {
    const meta = STITCH_SHAPE_ICONS[key];
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "shape-tile";
    if (key === selected) tile.classList.add("selected");
    tile.title = meta.label;
    tile.setAttribute("aria-label", meta.label);
    tile.innerHTML = meta.svg;
    tile.addEventListener("click", () => onSelect(key));
    grid.appendChild(tile);
  }
  wrap.appendChild(grid);
  return wrap;
}

/** Two-state direction toggle shared by slant and diamond chisels. Slant
 *  stores the actual angle in degrees (−45° / +45°); diamond reuses the
 *  same field as a sign-only signal — its magnitude doesn't change the
 *  rhombus geometry, but the sign mirrors which way the long diagonal
 *  leans across the seam. */
/** Clockwise / counter-clockwise segmented toggle for a seam's traversal
 *  direction (#796) — which way the row is walked when placing holes. Same
 *  preset-chip idiom as `slantDirectionToggle`, with immediate visual reflect. */
export function directionToggle(
  current: "cw" | "ccw",
  onChange: (dir: "cw" | "ccw") => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = "Stitch direction";
  wrap.appendChild(lbl);

  const row = document.createElement("div");
  row.className = "preset-row";

  const options: Array<{ label: string; value: "cw" | "ccw" }> = [
    { label: "Clockwise", value: "cw" },
    { label: "Counter-clockwise", value: "ccw" },
  ];
  const buttons: HTMLButtonElement[] = [];
  const reflect = (value: "cw" | "ccw") => {
    for (let i = 0; i < buttons.length; i++) {
      buttons[i]!.classList.toggle("active", options[i]!.value === value);
    }
  };
  for (const o of options) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "preset-chip";
    btn.textContent = o.label;
    btn.addEventListener("click", () => {
      reflect(o.value);
      onChange(o.value);
    });
    buttons.push(btn);
    row.appendChild(btn);
  }
  reflect(current);

  wrap.appendChild(row);
  return wrap;
}

export function slantDirectionToggle(
  current: number,
  onChange: (deg: number) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = "Direction";
  wrap.appendChild(lbl);

  const row = document.createElement("div");
  row.className = "preset-row";

  // "Regular" = the default seen on most off-the-shelf irons (top of the
  // mark leans forward in the direction of travel — negative slantAngle
  // in our screen-space convention). "Inverse" mirrors it.
  const options: Array<{ label: string; value: number }> = [
    { label: "Regular", value: -45 },
    { label: "Inverse", value: 45 },
  ];
  const buttons: HTMLButtonElement[] = [];
  const reflect = (value: number) => {
    const regular = value < 0;
    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i]!;
      const opt = options[i]!;
      btn.classList.toggle("active", opt.value < 0 === regular);
    }
  };
  for (const o of options) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "preset-chip";
    btn.textContent = o.label;
    btn.addEventListener("click", () => {
      // Update the visual immediately — the parent panel may or may not
      // refresh after the model commits, so don't rely on it.
      reflect(o.value);
      onChange(o.value);
    });
    buttons.push(btn);
    row.appendChild(btn);
  }
  reflect(current);

  wrap.appendChild(row);
  return wrap;
}

interface PitchFieldOpts {
  /** Active pitch-measurement mode — drives the field label so the user
   *  can read what the number means at a glance. */
  mode: StitchPitchMode;
  /** Two-button toggle between center / gap. */
  onModeChange: (mode: StitchPitchMode) => void;
  /** Receives a new pitch value.
   *  - `modeOverride` (when set) forces the mode too — SPI presets always
   *    pass "center" because Stitches Per Inch is inherently
   *    center-to-center.
   *  - `fromPreset` is true when the click came from one of the mm / SPI
   *    chip rows (vs. the number input). The handler uses this to also
   *    auto-scale the tooth width for slant chisels — but only when the
   *    user hasn't already manually edited the tooth. */
  onPitchChange: (
    v: number,
    modeOverride?: StitchPitchMode,
    fromPreset?: boolean,
  ) => void;
  /** Optional handler for the small "↺" reset button to the right of the
   *  input. When provided, the button is rendered and clicking it resets
   *  every chisel parameter (pitch, mode, tooth/size, slant direction) to
   *  the defaults for the current chisel shape. */
  onResetToDefaults?: () => void;
}

export function stitchPitchField(
  pitch: number,
  opts: PitchFieldOpts,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";

  const lbl = document.createElement("label");
  lbl.textContent =
    opts.mode === "gap"
      ? "Pitch (mm) — gap between holes"
      : "Pitch (mm) — center-to-center";
  wrap.appendChild(lbl);

  // mm / inches input pair lives directly under the label so the user's
  // first action — typing a value — is right where they expect it.
  // Slider has been removed; presets below cover quick selection.
  const inputRow = document.createElement("div");
  inputRow.className = "field-row stitch-pitch-input-row";
  const { row: pair } = mmInchInputs(
    pitch,
    (v) => opts.onPitchChange(v, undefined, false),
    { minMm: 0.5, maxMm: 20, mmStep: 0.05 },
  );
  inputRow.appendChild(pair);

  if (opts.onResetToDefaults) {
    const reset = document.createElement("button");
    reset.type = "button";
    reset.className = "stitch-reset-btn";
    reset.title = "Reset all stitch settings to the chisel's defaults";
    reset.setAttribute("aria-label", "Reset to defaults");
    reset.textContent = "↺";
    reset.addEventListener("click", () => opts.onResetToDefaults?.());
    inputRow.appendChild(reset);
  }
  wrap.appendChild(inputRow);

  // Two-state toggle for what the pitch number means. Selecting "SPI"
  // chips below forces this back to center automatically.
  const modeRow = document.createElement("div");
  modeRow.className = "preset-row";
  const modeOptions: Array<{ label: string; value: StitchPitchMode }> = [
    { label: "Centers", value: "center" },
    { label: "Gap", value: "gap" },
  ];
  for (const m of modeOptions) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "preset-chip";
    btn.textContent = m.label;
    if (m.value === opts.mode) btn.classList.add("active");
    btn.addEventListener("click", () => opts.onModeChange(m.value));
    modeRow.appendChild(btn);
  }
  wrap.appendChild(modeRow);

  const matches = (a: number, b: number) => Math.abs(a - b) < 0.01;

  const presetRow = (
    title: string,
    items: Array<{ label: string; value: number }>,
    forceMode?: StitchPitchMode,
  ) => {
    const row = document.createElement("div");
    row.className = "preset-row";
    const head = document.createElement("span");
    head.className = "preset-row-label";
    head.textContent = title;
    row.appendChild(head);
    for (const item of items) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "preset-chip";
      btn.textContent = item.label;
      // Highlight when the active value + (forced) mode both match.
      const modeMatches = forceMode === undefined || forceMode === opts.mode;
      if (matches(pitch, item.value) && modeMatches)
        btn.classList.add("active");
      btn.title = `${item.value.toFixed(3).replace(/\.?0+$/, "")} mm`;
      btn.addEventListener("click", () =>
        opts.onPitchChange(item.value, forceMode, true),
      );
      row.appendChild(btn);
    }
    return row;
  };

  wrap.appendChild(presetRow("mm", STITCH_PITCH_PRESETS_MM));
  // SPI is inherently a center-to-center spec — clicking any of these
  // chips also flips the mode back to center.
  wrap.appendChild(presetRow("SPI", STITCH_PITCH_PRESETS_SPI, "center"));

  return wrap;
}

export function holeDiameterField(
  diameter: number,
  onChange: (newDiameter: number) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";

  const lbl = document.createElement("label");
  lbl.textContent = "Hole diameter (mm)";
  wrap.appendChild(lbl);

  const matches = (a: number, b: number) => Math.abs(a - b) < 0.005;

  const presetRow = (
    title: string,
    items: Array<{ label: string; value: number }>,
  ) => {
    const row = document.createElement("div");
    row.className = "preset-row";
    const head = document.createElement("span");
    head.className = "preset-row-label";
    head.textContent = title;
    row.appendChild(head);
    for (const item of items) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "preset-chip";
      btn.textContent = item.label;
      if (matches(diameter, item.value)) btn.classList.add("active");
      btn.title = `⌀${item.value.toFixed(4).replace(/\.?0+$/, "")} mm`;
      btn.addEventListener("click", () => onChange(item.value));
      row.appendChild(btn);
    }
    return row;
  };

  wrap.appendChild(
    presetRow(
      "mm",
      HOLE_PRESETS_MM.map((v) => ({ label: String(v), value: v })),
    ),
  );
  wrap.appendChild(presetRow("in", HOLE_PRESETS_INCH));

  // Slider + paired mm/inches inputs below the presets.
  const inputRow = document.createElement("div");
  inputRow.className = "field-row length-field-row";
  const range = document.createElement("input");
  range.type = "range";
  range.min = "0.4";
  range.max = "20";
  range.step = "0.1";
  range.value = String(diameter);
  const { row: pair, setValue } = mmInchInputs(diameter, onChange, {
    minMm: 0.4,
    maxMm: 20,
    mmStep: 0.1,
  });
  range.addEventListener("input", () => {
    const v = parseFloat(range.value);
    if (Number.isFinite(v) && v > 0) {
      setValue(v);
      onChange(v);
    }
  });
  inputRow.appendChild(range);
  inputRow.appendChild(pair);
  wrap.appendChild(inputRow);

  return wrap;
}

export function textField(
  label: string,
  value: string,
  onChange: (v: string) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  wrap.appendChild(lbl);
  const input = document.createElement("input");
  input.type = "text";
  input.value = value;
  const commit = () => {
    const trimmed = input.value.trim();
    if (trimmed && trimmed !== value) onChange(trimmed);
    else input.value = value;
  };
  input.addEventListener("change", commit);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
  });
  wrap.appendChild(input);
  return wrap;
}

export function dimensionField(
  label: string,
  value: number,
  onChange: (v: number) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  wrap.appendChild(lbl);
  const { row } = mmInchInputs(value, onChange, {
    minMm: 0.1,
    mmStep: 0.1,
    // Takes effect while you type (#40). Commit-on-blur is a keyboard
    // assumption: on a tablet the floating keyboard covers the canvas, so
    // nothing is visible until you dismiss it — and if the platform does not
    // deliver the `change` the field waits for, nothing happens at all.
    commitWhileTyping: true,
  });
  wrap.appendChild(row);
  return wrap;
}

export const MM_PER_INCH = 25.4;

/** Format helpers — keep the textual precision per unit so values still look
 *  natural after a one-way conversion. mm gets 1 decimal; inches get 3. */
export function formatMm(mm: number): string {
  return Number.isFinite(mm) ? mm.toFixed(1) : "";
}
export function formatIn(mm: number): string {
  return Number.isFinite(mm) ? (mm / MM_PER_INCH).toFixed(3) : "";
}

/**
 * Paired mm/inch text inputs that **allow an empty state** (representing
 * "no value entered"). Used for the shape/cutout dimension fields, where
 * blank means "read this axis from the cursor while drawing" and a typed
 * value locks the axis for the next placement.
 *
 * Differs from `mmInchInputs` in two ways:
 *   • Initial / committed value is `number | null` — clearing the input
 *     stays empty instead of reverting to the previous value.
 *   • Exposes `setLive(mm)` separate from `setValue(mm | null)`: live
 *     pushes update only the displayed text without touching the stored
 *     "preset" state, so a drag can mirror itself in the panel while a
 *     locked preset stays visible.
 */
/** A strict positive-integer preset field with the same label-row Reset chip
 *  as `presetDimensionField` (#928 follow-up). The input accepts digits only
 *  (everything else is stripped as you type), commits clamp to
 *  [`min`, `max`], and clearing / Reset commits `null` — "use the default".
 *  `placeholder` shows what the cleared state means (e.g. the default). */
export function presetIntegerField(
  label: string,
  initial: number | null,
  opts: { min: number; max: number; placeholder?: string },
  onChange: (v: number | null) => void,
): {
  row: HTMLElement;
  setValue: (v: number | null) => void;
  setLive: (v: number) => void;
} {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const labelRow = document.createElement("div");
  labelRow.className = "preset-dim-label-row";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  labelRow.appendChild(lbl);
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "preset-dim-reset";
  resetBtn.textContent = "Reset";
  resetBtn.title = "Clear (use the default)";
  labelRow.appendChild(resetBtn);
  wrap.appendChild(labelRow);

  const input = document.createElement("input");
  input.type = "text";
  input.inputMode = "numeric";
  input.autocomplete = "off";
  input.spellcheck = false;
  input.placeholder = opts.placeholder ?? "—";
  wrap.appendChild(input);

  let stored: number | null = initial;
  const syncResetVisibility = () => {
    resetBtn.style.display = stored == null ? "none" : "";
  };
  const renderStored = () => {
    input.value = stored == null ? "" : String(stored);
    syncResetVisibility();
  };
  renderStored();

  const commit = (next: number | null) => {
    if (next === stored) return;
    stored = next;
    onChange(next);
  };

  // Digits only, as you type — pasted or typed letters / signs / decimals are
  // stripped immediately so the field can never hold a non-integer.
  input.addEventListener("input", () => {
    const cleaned = input.value.replace(/[^0-9]/g, "");
    if (cleaned !== input.value) input.value = cleaned;
  });
  input.addEventListener("change", () => {
    const trimmed = input.value.trim();
    if (trimmed === "") {
      commit(null);
      renderStored();
      return;
    }
    const n = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(n)) {
      renderStored(); // unparseable — revert to the stored value
      return;
    }
    const clamped = Math.min(opts.max, Math.max(opts.min, n));
    commit(clamped);
    renderStored();
  });
  resetBtn.addEventListener("click", () => {
    commit(null);
    renderStored();
  });

  return {
    row: wrap,
    setValue: (v: number | null) => {
      stored = v;
      renderStored();
    },
    // Display-only mirror for an in-flight gesture (#931) — shows the value
    // without touching the stored preset; call setValue to revert.
    setLive: (v: number) => {
      input.value = String(v);
    },
  };
}

export function presetDimensionField(
  label: string,
  initialMm: number | null,
  onChange: (mm: number | null) => void,
  /** Derived value to advertise while the field is unset (#934) — e.g. the
   *  radius computed from Sides + Side length. Shown as the placeholder in
   *  both unit inputs; null keeps the plain em-dash. */
  placeholderMm?: number | null,
): {
  row: HTMLElement;
  setValue: (mm: number | null) => void;
  setLive: (mm: number) => void;
} {
  const wrap = document.createElement("div");
  wrap.className = "field";
  // Label + reset button live in a flex row so the "Reset" sits inline at
  // the right edge of the label, like a tiny chip. The reset is hidden when
  // no preset is set (nothing to clear) so the row stays clean for the
  // common "no preset, draw freely" case.
  const labelRow = document.createElement("div");
  labelRow.className = "preset-dim-label-row";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  labelRow.appendChild(lbl);
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "preset-dim-reset";
  resetBtn.textContent = "Reset";
  resetBtn.title = "Clear this dimension";
  labelRow.appendChild(resetBtn);
  wrap.appendChild(labelRow);

  const row = document.createElement("div");
  row.className = "mm-in-row";

  const mm = document.createElement("input");
  mm.type = "text";
  mm.inputMode = "decimal";
  mm.autocomplete = "off";
  mm.spellcheck = false;
  mm.placeholder = placeholderMm != null ? formatMm(placeholderMm) : "—";

  const inch = document.createElement("input");
  inch.type = "text";
  inch.inputMode = "decimal";
  inch.autocomplete = "off";
  inch.spellcheck = false;
  inch.placeholder = placeholderMm != null ? formatIn(placeholderMm) : "—";

  /** Last user-committed preset (writes onChange). Live drag updates do not
   *  touch this so the user's intent (empty vs. locked) is preserved. */
  let stored: number | null = initialMm;

  const syncResetVisibility = () => {
    resetBtn.style.display = stored == null ? "none" : "";
  };
  syncResetVisibility();

  const renderStored = () => {
    mm.value = stored == null ? "" : formatMm(stored);
    inch.value = stored == null ? "" : formatIn(stored);
  };
  renderStored();

  const parsePositive = (
    raw: string,
    fieldUnit: "mm" | "in",
  ): number | null => {
    const trimmed = raw.trim();
    if (trimmed === "") return null;
    // `parseLengthInput` accepts decimals, simple fractions
    // (`1/8`), mixed fractions (`1 1/2`, `1-1/2`), and explicit
    // unit suffixes (`1 in`, `5 cm`, `0.5 m`) — the suffix is
    // converted into the field's labelled unit so the value can
    // be treated as if the user had typed in that unit. Returning
    // `NaN as null` mirrors the original sentinel for "input was
    // not empty but didn't parse to a positive value" — the
    // caller keeps the prior stored value rather than committing.
    const n = parseLengthInput(trimmed, fieldUnit);
    if (n === null) return NaN as unknown as null;
    return n > 0 ? n : (NaN as unknown as null);
  };

  const commit = (next: number | null) => {
    if (next === stored) return;
    stored = next;
    onChange(next);
    syncResetVisibility();
  };

  // Typing is not finished until the user stops. `1/4` passes through `1`, and
  // committing that intermediate pushed a bogus preset to the tool and flipped
  // the paired unit cell to a wrong value mid-keystroke. So the per-keystroke
  // path is DEBOUNCED: keep the liveness (no need to blur or press Enter) but
  // wait for a pause, and let `change` / Enter flush immediately.
  const TYPING_IDLE_MS = 400;
  let pendingTimer: ReturnType<typeof setTimeout> | null = null;
  const cancelPending = () => {
    if (pendingTimer === null) return;
    clearTimeout(pendingTimer);
    pendingTimer = null;
  };
  const scheduleWhileTyping = (fn: () => void) => {
    cancelPending();
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      // The panel re-renders freely; a timer that outlived its input must not
      // resurrect a stale value.
      if (!mm.isConnected && !inch.isConnected) return;
      fn();
    }, TYPING_IDLE_MS);
  };

  resetBtn.addEventListener("click", () => {
    cancelPending();
    commit(null);
    renderStored();
  });

  mm.addEventListener("change", () => {
    cancelPending();
    const parsed = parsePositive(mm.value, "mm");
    if (parsed === null) {
      commit(null);
      renderStored();
      return;
    }
    if (Number.isNaN(parsed)) {
      renderStored();
      return;
    }
    commit(parsed);
    mm.value = formatMm(parsed);
    inch.value = formatIn(parsed);
  });
  inch.addEventListener("change", () => {
    cancelPending();
    const parsed = parsePositive(inch.value, "in");
    if (parsed === null) {
      commit(null);
      renderStored();
      return;
    }
    if (Number.isNaN(parsed)) {
      renderStored();
      return;
    }
    const mmVal = parsed * MM_PER_INCH;
    commit(mmVal);
    mm.value = formatMm(mmVal);
    inch.value = formatIn(mmVal);
  });
  // Live cross-unit sync + commit without needing to blur or press Enter, but
  // only once typing pauses (see `scheduleWhileTyping`). The source field is
  // never rewritten here — only the PAIRED unit cell — so the caret and any
  // half-typed fraction survive.
  mm.addEventListener("input", () => {
    const trimmed = mm.value.trim();
    if (trimmed === "") {
      scheduleWhileTyping(() => {
        inch.value = "";
        commit(null);
      });
      return;
    }
    const n = parseLengthInput(trimmed, "mm");
    if (n !== null && n > 0) {
      scheduleWhileTyping(() => {
        inch.value = formatIn(n);
        commit(n);
      });
    }
  });
  inch.addEventListener("input", () => {
    const trimmed = inch.value.trim();
    if (trimmed === "") {
      scheduleWhileTyping(() => {
        mm.value = "";
        commit(null);
      });
      return;
    }
    const n = parseLengthInput(trimmed, "in");
    if (n !== null && n > 0) {
      scheduleWhileTyping(() => {
        const mmVal = n * MM_PER_INCH;
        mm.value = formatMm(mmVal);
        commit(mmVal);
      });
    }
  });
  // Enter leaves the field — or, for a field focused by touch, moves on to
  // the next length field so the on-screen keyboard stays up (#1151).
  wireLengthFieldEnter(mm);
  wireLengthFieldEnter(inch);

  const cells: HTMLElement[] = [
    makeUnitCell(mm, "mm"),
    makeUnitCell(inch, "in"),
  ];
  if (getUnits() === "in") cells.reverse();
  const visibleCells = getShowSecondaryUnits() ? cells : [cells[0]!];
  for (const cell of visibleCells) row.appendChild(cell);

  wrap.appendChild(row);

  return {
    row: wrap,
    setValue: (next) => {
      cancelPending();
      stored = next;
      renderStored();
      syncResetVisibility();
    },
    setLive: (mmVal) => {
      // Display-only update — preserves the empty `stored` so the user's
      // "no preset" intent survives a live drag.
      if (stored != null) return;
      if (!Number.isFinite(mmVal)) {
        mm.value = "";
        inch.value = "";
        return;
      }
      mm.value = formatMm(mmVal);
      inch.value = formatIn(mmVal);
    },
  };
}

/** Compact crosshair + arc icon for an angle value (degrees,
 *  visual CCW from horizontal right). Same visual language as the
 *  snap-angle chip in the canvas overlay so the user reads the
 *  in-panel angle the same way they read the on-canvas snap angle.
 *  Two ranges are rendered — one folded to the [0, 90] horizontal
 *  measurement, one full [0, 360) sweep — matching the chip pair
 *  the snap indicator pushes onto the canvas. */
export function buildPanelAngleIconSvg(
  visualDeg: number,
  mode: "measurement" | "full",
): string {
  const GREY = cssVar("--compass-axis");
  const BLUE = cssVar("--compass-active");
  // The chip in app.ts works in code-space (y-down) angles. Convert
  // the panel's "visual" degrees back to that convention so the icon
  // geometry matches byte-for-byte.
  const codeDeg = (360 - (((visualDeg % 360) + 360) % 360)) % 360;
  let bounds: [number, number];
  if (codeDeg < 90) bounds = [0, 90];
  else if (codeDeg < 180) bounds = [90, 180];
  else if (codeDeg < 270) bounds = [180, 270];
  else bounds = [270, 360];
  const baselineDeg = codeDeg <= 90 || codeDeg >= 270 ? 0 : 180;
  const r = (d: number) => (d * Math.PI) / 180;
  const ray = (d: number) =>
    [10 + 8 * Math.cos(r(d)), 10 + 8 * Math.sin(r(d))] as const;
  const line = (d: number, color: string, width: number) => {
    const [x, y] = ray(d);
    return `<line x1="10" y1="10" x2="${x.toFixed(2)}" y2="${y.toFixed(2)}" stroke="${color}" stroke-width="${width}" stroke-linecap="round"/>`;
  };
  const b0 = bounds[0] % 360;
  const b1 = bounds[1] % 360;
  const highlight = mode === "measurement";
  const axes = [0, 90, 180, 270]
    .map((d) => line(d, GREY, highlight && (d === b0 || d === b1) ? 1.4 : 0.7))
    .join("");
  const ARC_R = 4.5;
  let arc = "";
  if (mode === "measurement") {
    if (codeDeg !== baselineDeg) {
      const sx = 10 + ARC_R * Math.cos(r(baselineDeg));
      const sy = 10 + ARC_R * Math.sin(r(baselineDeg));
      const ex = 10 + ARC_R * Math.cos(r(codeDeg));
      const ey = 10 + ARC_R * Math.sin(r(codeDeg));
      const sweep = (codeDeg - baselineDeg + 360) % 360 < 180 ? 1 : 0;
      arc = `<path d="M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${ARC_R} ${ARC_R} 0 0 ${sweep} ${ex.toFixed(2)} ${ey.toFixed(2)}" fill="none" stroke="${BLUE}" stroke-width="1.2"/>`;
    }
  } else {
    const sweepVisual = (360 - codeDeg) % 360;
    if (sweepVisual !== 0) {
      const sx = 10 + ARC_R;
      const sy = 10;
      const ex = 10 + ARC_R * Math.cos(r(codeDeg));
      const ey = 10 + ARC_R * Math.sin(r(codeDeg));
      const largeArc = sweepVisual > 180 ? 1 : 0;
      arc = `<path d="M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${ARC_R} ${ARC_R} 0 ${largeArc} 0 ${ex.toFixed(2)} ${ey.toFixed(2)}" fill="none" stroke="${BLUE}" stroke-width="1.2"/>`;
    }
  }
  const blueBaselineDeg = mode === "full" ? 0 : baselineDeg;
  return `<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">${axes}${line(blueBaselineDeg, BLUE, 1.4)}${line(codeDeg, BLUE, 1.6)}${arc}</svg>`;
}

/** Fold a visual (CCW-from-right, y-up) angle to the [0, 90]
 *  measurement-from-nearest-horizontal range that the snap-angle
 *  chip's main readout uses. */
export function foldVisualDegToHorizontal(visualDeg: number): number {
  const wrapped = ((visualDeg % 180) + 180) % 180;
  return Math.round(wrapped > 90 ? 180 - wrapped : wrapped);
}

/** Single nullable degrees input with preset chips (0 / 30 / 45 / 60
 *  / 90) and the same folded + full angle indicators the canvas
 *  snap-angle chip uses. Returns a row + setLive shim so a parent
 *  tool can mirror its live drag state without yanking the user's
 *  typed value when one is set.
 *
 *  Interpretation of the entered value is up to the caller: the
 *  Construction tool reads the tool-mode value as an angle increment
 *  (canvas drag snaps to multiples of it), while the per-line
 *  primitive panel treats it as an absolute angle to set the line
 *  to. The field itself is dumb about which mode applies. */
/** Unified scrubber + number control (issue #529): a range slider that
 *  fills the row on the left and a fixed-width number input (with an
 *  optional unit suffix) on the right — no separate value label, the
 *  input *is* the readout. Shared by the tracing-image opacity +
 *  rotation fields and the construction-line rotation field so every
 *  scrubber looks and behaves the same.
 *
 *  `onLive` fires continuously while dragging the slider / typing (for
 *  a no-history live preview); `onCommit` fires on release / blur (the
 *  history commit). `setValue` lets a parent mirror an external live
 *  value (e.g. a canvas rotation drag) into both inputs without firing
 *  either callback. */
export function scrubberInputField(opts: {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  suffix?: string;
  ariaLabel?: string;
  onLive?: (v: number) => void;
  onCommit: (v: number) => void;
}): { wrap: HTMLElement; setValue: (v: number) => void } {
  const step = opts.step ?? 1;
  const clamp = (v: number) => Math.max(opts.min, Math.min(opts.max, v));
  const round = (v: number) =>
    step >= 1 ? Math.round(v) : Math.round(v / step) * step;
  const initial = round(clamp(opts.value));

  const wrap = document.createElement("div");
  wrap.className = "field";
  const label = document.createElement("label");
  label.className = "field-label";
  label.textContent = opts.label;
  wrap.appendChild(label);

  const row = document.createElement("div");
  row.className = "field-row scrubber-row";

  const range = document.createElement("input");
  range.type = "range";
  range.min = String(opts.min);
  range.max = String(opts.max);
  range.step = String(step);
  range.value = String(initial);
  range.setAttribute("aria-label", opts.ariaLabel ?? opts.label);

  const valueWrap = document.createElement("div");
  valueWrap.className = "scrubber-value";
  const num = document.createElement("input");
  num.type = "number";
  num.min = String(opts.min);
  num.max = String(opts.max);
  num.step = String(step);
  num.value = String(initial);
  num.setAttribute(
    "aria-label",
    `${opts.ariaLabel ?? opts.label}${opts.suffix ? ` (${opts.suffix})` : ""}`,
  );
  valueWrap.appendChild(num);
  if (opts.suffix) {
    const suffix = document.createElement("span");
    suffix.className = "scrubber-suffix";
    suffix.textContent = opts.suffix;
    valueWrap.appendChild(suffix);
  }

  range.addEventListener("input", () => {
    const v = parseFloat(range.value);
    if (!Number.isFinite(v)) return;
    num.value = String(round(v));
    opts.onLive?.(v);
  });
  range.addEventListener("change", () => {
    const v = parseFloat(range.value);
    if (Number.isFinite(v)) opts.onCommit(clamp(v));
  });
  num.addEventListener("input", () => {
    const v = parseFloat(num.value);
    if (!Number.isFinite(v)) return;
    range.value = String(clamp(v));
    opts.onLive?.(v);
  });
  num.addEventListener("change", () => {
    const v = parseFloat(num.value);
    if (!Number.isFinite(v)) return;
    const c = clamp(v);
    num.value = String(round(c));
    range.value = String(c);
    opts.onCommit(c);
  });

  row.appendChild(range);
  row.appendChild(valueWrap);
  wrap.appendChild(row);

  const setValue = (v: number) => {
    const s = String(round(clamp(v)));
    if (range.value !== s) range.value = s;
    if (num.value !== s) num.value = s;
  };
  return { wrap, setValue };
}

export function presetAngleField(
  initialDeg: number | null,
  onChange: (deg: number | null) => void,
  opts: { label?: string; resetTitle?: string } = {},
): {
  row: HTMLElement;
  setValue: (deg: number | null) => void;
  setLive: (deg: number) => void;
} {
  const PRESETS = [0, 30, 45, 60, 90];

  const wrap = document.createElement("div");
  wrap.className = "field line-angle-field";
  wrap.dataset.lineAngleField = "true";

  const labelRow = document.createElement("div");
  labelRow.className = "preset-dim-label-row";
  const lbl = document.createElement("label");
  lbl.textContent = opts.label ?? "Angle";
  labelRow.appendChild(lbl);
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "preset-dim-reset";
  resetBtn.textContent = "Reset";
  resetBtn.title = opts.resetTitle ?? "Clear the locked angle";
  labelRow.appendChild(resetBtn);
  wrap.appendChild(labelRow);

  const inputRow = document.createElement("div");
  inputRow.className = "line-angle-input-row";

  const inputCell = document.createElement("div");
  inputCell.className = "line-angle-input-cell";
  const degInput = document.createElement("input");
  degInput.type = "text";
  degInput.autocomplete = "off";
  degInput.spellcheck = false;
  degInput.placeholder = "—";
  degInput.dataset.role = "line-angle-input";
  inputCell.appendChild(degInput);
  const degSuffix = document.createElement("span");
  degSuffix.className = "unit-label";
  degSuffix.textContent = "°";
  inputCell.appendChild(degSuffix);
  inputRow.appendChild(inputCell);

  // Indicator pair — folded + full angles, mirroring the on-canvas
  // chip-pair. Both fill in once the angle becomes non-null.
  const indicatorPair = document.createElement("div");
  indicatorPair.className = "line-angle-indicator-pair";
  const indicatorFolded = document.createElement("span");
  indicatorFolded.className = "snap-angle-indicator";
  indicatorFolded.dataset.role = "line-angle-indicator-folded";
  const indicatorFull = document.createElement("span");
  indicatorFull.className = "snap-angle-indicator snap-angle-indicator-full";
  indicatorFull.dataset.role = "line-angle-indicator-full";
  indicatorPair.appendChild(indicatorFolded);
  indicatorPair.appendChild(indicatorFull);
  inputRow.appendChild(indicatorPair);

  wrap.appendChild(inputRow);

  // Preset chip row (0 / 30 / 45 / 60 / 90).
  const chipRow = document.createElement("div");
  chipRow.className = "preset-chips line-angle-chips";
  const chipBtns = new Map<number, HTMLButtonElement>();
  for (const deg of PRESETS) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "preset-chip";
    chip.textContent = `${deg}°`;
    chip.dataset.angleDeg = String(deg);
    chip.addEventListener("click", () => {
      commit(deg);
      renderStored();
    });
    chipRow.appendChild(chip);
    chipBtns.set(deg, chip);
  }
  wrap.appendChild(chipRow);

  let stored: number | null = initialDeg;

  const formatDeg = (deg: number): string => {
    const rounded = Math.round(deg * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}` : `${rounded.toFixed(1)}`;
  };

  const renderIndicators = (deg: number | null) => {
    if (deg == null) {
      indicatorFolded.innerHTML = "";
      indicatorFull.innerHTML = "";
      return;
    }
    indicatorFolded.innerHTML =
      buildPanelAngleIconSvg(deg, "measurement") +
      `<span class="snap-angle-value">${foldVisualDegToHorizontal(deg)}°</span>`;
    const wrappedFull = ((deg % 360) + 360) % 360;
    if (wrappedFull <= 90) {
      // Folded value matches the full value when angle is in the upper-right
      // quadrant — same suppression rule the canvas chip pair uses.
      indicatorFull.innerHTML = "";
    } else {
      indicatorFull.innerHTML =
        buildPanelAngleIconSvg(deg, "full") +
        `<span class="snap-angle-value">${Math.round(wrappedFull)}°</span>`;
    }
  };

  const syncChipsAndReset = () => {
    resetBtn.style.display = stored == null ? "none" : "";
    for (const [deg, chip] of chipBtns) {
      const active = stored != null && Math.abs(deg - stored) < 0.01;
      chip.classList.toggle("active", active);
    }
  };

  const renderStored = () => {
    degInput.value = stored == null ? "" : formatDeg(stored);
    renderIndicators(stored);
    syncChipsAndReset();
  };
  renderStored();

  const commit = (next: number | null) => {
    if (next === stored) return;
    stored = next;
    onChange(next);
    syncChipsAndReset();
  };

  resetBtn.addEventListener("click", () => {
    commit(null);
    renderStored();
  });

  const parseDeg = (raw: string): number | null => {
    const trimmed = raw.trim().replace(/°\s*$/, "");
    if (trimmed === "") return null;
    const n = parseFloat(trimmed);
    if (!Number.isFinite(n)) return NaN as unknown as number;
    // Wrap to [0, 360) so a typed value like 450 collapses sensibly.
    let wrapped = n % 360;
    if (wrapped < 0) wrapped += 360;
    return wrapped;
  };

  degInput.addEventListener("input", () => {
    const trimmed = degInput.value.trim();
    if (trimmed === "") {
      commit(null);
      renderIndicators(null);
      return;
    }
    const parsed = parseDeg(degInput.value);
    if (parsed == null || Number.isNaN(parsed)) return;
    commit(parsed);
    renderIndicators(parsed);
  });
  degInput.addEventListener("change", () => {
    const parsed = parseDeg(degInput.value);
    if (parsed == null) {
      commit(null);
      renderStored();
      return;
    }
    if (Number.isNaN(parsed)) {
      renderStored();
      return;
    }
    commit(parsed);
    renderStored();
  });
  degInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
  });

  return {
    row: wrap,
    setValue: (next) => {
      stored = next;
      renderStored();
    },
    setLive: (deg) => {
      if (stored != null) return;
      if (!Number.isFinite(deg)) {
        degInput.value = "";
        renderIndicators(null);
        return;
      }
      degInput.value = formatDeg(deg);
      renderIndicators(deg);
    },
  };
}

/**
 * Slider + mm/inches paired inputs for a length-valued setting (mm). Used by
 * stamp spacing, offset distance, brush size, stitch hole size, etc. — the
 * mm-flavoured equivalents of `numberField`. Pass `noSlider: true` to drop
 * the range scrubber and render only the mm/in pair (used by stitching
 * fields where the user prefers typed values + presets).
 */
export function lengthField(
  label: string,
  value: number,
  minMm: number,
  maxMm: number,
  mmStep: number,
  onChange: (mm: number) => void,
  opts: {
    allowNegative?: boolean;
    noSlider?: boolean;
    /** Fire `onChange` per-keystroke instead of waiting for blur /
     *  Enter. See `MmInchOptions.liveCommit` for the trade-off. */
    liveCommit?: boolean;
    /** Optional out-channel for the field's setValue function — the
     *  caller passes a callback that gets invoked once with the
     *  setter, which it can store and later use to push live values
     *  into the input (without firing onChange). Used by the per-
     *  line panel to mirror canvas vertex drags into the Length
     *  field in real time. */
    captureSetValue?: (setValue: (mm: number) => void) => void;
  } = {},
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  wrap.appendChild(lbl);

  const { row: pair, setValue } = mmInchInputs(value, onChange, {
    minMm,
    maxMm,
    mmStep,
    allowNegative: opts.allowNegative,
    liveCommit: opts.liveCommit,
  });
  opts.captureSetValue?.(setValue);

  if (opts.noSlider) {
    wrap.appendChild(pair);
    return wrap;
  }

  const row = document.createElement("div");
  row.className = "field-row length-field-row";

  const range = document.createElement("input");
  range.type = "range";
  range.min = String(minMm);
  range.max = String(maxMm);
  range.step = String(mmStep);
  range.value = String(value);

  range.addEventListener("input", () => {
    const v = parseFloat(range.value);
    if (!Number.isFinite(v)) return;
    setValue(v);
    onChange(v);
  });

  row.appendChild(range);
  row.appendChild(pair);
  wrap.appendChild(row);
  return wrap;
}

/** "Working area" control (#199) built on the proposed `<rangegroup>` element
 *  (polyfilled as `<range-group>`): a two-thumb range whose heads sit on a
 *  `[0, total]` arc-length track — the span between them is where holes go —
 *  with our dual-unit (mm/in) **Start** / **End** position inputs side by side
 *  below it, two-way bound to the thumbs. Reports the insets from each end
 *  (`startInset` = start head; `endInset` = total − end head): `onLive` fires
 *  continuously while dragging (no-history canvas preview), `onChange` once on
 *  release / typed commit. If the custom element is never upgraded, the two
 *  range inputs degrade to plain, still-functional sliders. */
export function stitchWorkingAreaField(
  total: number,
  startInset: number,
  endInset: number,
  step: number,
  onChange: (startInset: number, endInset: number) => void,
  onLive?: (startInset: number, endInset: number) => void,
): HTMLElement {
  const clamp = (v: number) => Math.max(0, Math.min(total, v));
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = "Working area";
  lbl.title = "Drag the heads (or type a start / end) to inset the stitching";
  wrap.appendChild(lbl);

  const group = document.createElement("range-group") as RangeGroupElement;
  group.setAttribute("min", "0");
  group.setAttribute("max", String(total));
  const mkThumb = (value: number, aria: string) => {
    const r = document.createElement("input");
    r.type = "range";
    r.min = "0";
    r.max = String(total);
    // `step="any"` (continuous), NOT the input step: a quantized thumb snaps a
    // typed value to the nearest step, and because the End head is read back as
    // `total − hi`, a value on a half-step boundary (1/8" = 3.175 mm) rounds the
    // start up and the end down — so the two inputs showed 0.126" vs 0.124" for
    // the same 1/8". Continuous thumbs keep typed values exact and symmetric.
    r.step = "any";
    r.value = String(clamp(value));
    r.setAttribute("aria-label", aria);
    return r;
  };
  // Thumb 0 = start position (= startInset); thumb 1 = end position
  // (= total − endInset).
  group.appendChild(mkThumb(startInset, "Stitching start"));
  group.appendChild(mkThumb(total - endInset, "Stitching end"));

  // Insets derived from the two thumb positions.
  const insetsFrom = (lo: number, hi: number) => ({
    start: clamp(lo),
    end: total - clamp(hi),
  });

  // Our own dual-unit inputs, side by side below the track. Start shows the
  // distance from the START of the seam (= the low thumb position); End shows
  // the distance from the END (= total − the high thumb position).
  const startCell = mmInchInputs(
    startInset,
    (mm) => group.setRangeValue(0, clamp(mm)),
    { minMm: 0, maxMm: total, mmStep: step },
  );
  const endCell = mmInchInputs(
    endInset,
    (mm) => group.setRangeValue(1, clamp(total - mm)),
    { minMm: 0, maxMm: total, mmStep: step },
  );

  // Centered readout of the working-area length (the span between the heads),
  // updated live as they move.
  const lengthEl = document.createElement("div");
  lengthEl.className = "working-area-length";
  // Format in the user's active unit (mm / in), matching the dual-unit inputs.
  const fmtLength = (mm: number) =>
    getUnits() === "in"
      ? `${(mm / 25.4).toFixed(3)} in`
      : `${mm.toFixed(1)} mm`;
  const renderLength = () => {
    // Before the <range-group> is upgraded (added to the DOM) `values` is empty,
    // so fall back to the initial inset positions rather than the full length.
    const v = group.values;
    const lo = v.length >= 2 ? v[0]! : clamp(startInset);
    const hi = v.length >= 2 ? v[1]! : clamp(total - endInset);
    const len = Math.max(0, hi - lo);
    lengthEl.textContent = fmtLength(len);
  };
  renderLength();

  const syncCells = () => {
    const [lo, hi] = group.values;
    startCell.setValue(lo ?? 0);
    endCell.setValue(total - (hi ?? total));
    renderLength();
  };
  group.addEventListener("input", () => {
    const [lo, hi] = group.values;
    syncCells();
    const { start, end } = insetsFrom(lo ?? 0, hi ?? total);
    onLive?.(start, end);
  });
  group.addEventListener("change", () => {
    const [lo, hi] = group.values;
    const { start, end } = insetsFrom(lo ?? 0, hi ?? total);
    onChange(start, end);
  });

  // Labels row: "Start" (left) · working-area length (centered) · "End" (right).
  const labelsRow = document.createElement("div");
  labelsRow.className = "field-row working-area-labels";
  const startLbl = document.createElement("label");
  startLbl.textContent = "Start";
  const endLbl = document.createElement("label");
  endLbl.className = "working-area-label--end";
  endLbl.textContent = "End";
  labelsRow.appendChild(startLbl);
  labelsRow.appendChild(lengthEl);
  labelsRow.appendChild(endLbl);

  // Fields row: the two dual-unit inputs underneath their labels.
  const fieldsRow = document.createElement("div");
  fieldsRow.className = "field-row working-area-fields";
  const fieldCell = (inner: HTMLElement) => {
    const g = document.createElement("div");
    g.className = "working-area-input";
    g.appendChild(inner);
    return g;
  };
  fieldsRow.appendChild(fieldCell(startCell.row));
  fieldsRow.appendChild(fieldCell(endCell.row));

  const inputs = document.createElement("div");
  inputs.className = "working-area-inputs";
  inputs.appendChild(labelsRow);
  inputs.appendChild(fieldsRow);

  wrap.appendChild(group);
  wrap.appendChild(inputs);
  return wrap;
}

/** The caution shown beside a Smoothness control once the object has been
 *  hand-edited (#248): re-applying the smoothing rebuilds the shape from the
 *  original trace, so the edits made since go with it.
 *
 *  A note rather than a confirmation dialog on purpose. This is a slider —
 *  a prompt on every drag would be unusable — and re-smoothing is an ordinary
 *  undoable commit, so the cost of finding out by doing is one Ctrl+Z. */
export const RESMOOTH_DISCARDS_EDITS_NOTE =
  "Re-applying smoothness rebuilds this from the original trace — the point edits you've made since will go.";

/** A "Dimensions" block for the foot of an object's panel (#155): the measured
 *  size of the thing selected, kept together and out of the way of the controls
 *  above it. A readout is not a control — mixed in among the fields it reads as
 *  something you can set, and it competes for the eye with the ones you can. */
export function dimensionsSection(rows: string[]): HTMLElement | null {
  if (rows.length === 0) return null;
  // Nothing special about it beyond being last (#1050). It used to carry a
  // `dimensions-section` class with its own top margin — dead by then, since
  // the shared `#properties-panel .panel-section` rule outranks a bare class
  // and had already zeroed the margin it set. Address it as `[data-section]`
  // like any other.
  const wrap = panelSection("Dimensions");
  for (const row of rows) wrap.appendChild(fieldNote(row));
  return wrap;
}

/**
 * One titled section of a properties panel — a real `<section>`, because that
 * is what it is: a titled run of related controls, separated from its
 * neighbours by a rule that spans the sidebar.
 *
 * `<section>` is for exactly this and nothing else (#1050): a TOP-LEVEL band of
 * the sidebar with a rule above it. A group of related fields WITHIN a section
 * is a `<div>` — nesting a `<section>` inside a `<section>` claims a document
 * outline the sidebar doesn't have, and drew a second rule partway down a
 * panel where no boundary existed.
 *
 * Every section in the panel goes through here so the separator, the spacing,
 * the heading level and the addressing are decided once. There were eight
 * near-copies of this before, which is how six of them ended up at `h5` —
 * skipping a level under the panel's `h3` — while the other two were `h4`.
 */
export function panelSection(title: string): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "panel-section";
  // Every section is addressable by what it IS rather than by a class added
  // for the purpose. Bespoke hook classes read as styling and get "cleaned up"
  // by whoever finds no CSS for them.
  wrap.dataset.section = panelSectionSlug(title);
  const heading = document.createElement("h4");
  heading.className = "panel-section-heading";
  heading.textContent = title;
  wrap.appendChild(heading);
  return wrap;
}

/** A section title as a stable attribute value: "Corner holes" →
 *  "corner-holes". Titles are authored in this file's own calls, so this only
 *  has to survive the words we actually use. */
export function panelSectionSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** A small dim caption under a field. */
export function fieldNote(text: string): HTMLParagraphElement {
  const el = document.createElement("p");
  el.className = "field-note";
  el.textContent = text;
  return el;
}

/** Closed-loop "Start of path" control (#199): a single range over `[0, total]`
 *  arc-length setting where the stitching starts counting from (the working
 *  area is measured from there). Cardinal tick-marks (top / right / bottom /
 *  left of the loop) are clickable to snap the start to them. `onLive` fires
 *  while dragging (no-history preview), `onChange` on release / typed / snap. */
export function stitchStartOfPathField(
  total: number,
  startOffset: number,
  ticks: { top: number; bottom: number; left: number; right: number } | null,
  step: number,
  onChange: (startOffset: number) => void,
  onLive?: (startOffset: number) => void,
): HTMLElement {
  const clamp = (v: number) => Math.max(0, Math.min(total, v));
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = "Start of path";
  lbl.title =
    "Where the stitches start counting from (drag, type, or snap to a side)";
  wrap.appendChild(lbl);

  const track = document.createElement("div");
  track.className = "start-of-path";

  const range = document.createElement("input");
  range.type = "range";
  range.className = "start-of-path-range";
  range.min = "0";
  range.max = String(total);
  range.step = String(step);
  range.value = String(clamp(startOffset));

  const cell = mmInchInputs(
    startOffset,
    (mm) => {
      range.value = String(clamp(mm));
      onChange(clamp(mm));
    },
    { minMm: 0, maxMm: total, mmStep: step },
  );

  const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0);
  // Cardinal snap ticks. Clicking one moves the start to that side of the loop.
  const tickDefs: Array<[string, number]> = ticks
    ? [
        ["T", ticks.top],
        ["R", ticks.right],
        ["B", ticks.bottom],
        ["L", ticks.left],
      ]
    : [];
  for (const [glyph, pos] of tickDefs) {
    const tick = document.createElement("button");
    tick.type = "button";
    tick.className = "start-of-path-tick";
    tick.style.left = `${pct(pos)}%`;
    tick.textContent = glyph;
    tick.title = `Start at the ${{ T: "top", R: "right", B: "bottom", L: "left" }[glyph]} of the loop`;
    tick.addEventListener("click", () => {
      range.value = String(clamp(pos));
      cell.setValue(clamp(pos));
      onChange(clamp(pos));
    });
    track.appendChild(tick);
  }
  track.appendChild(range);

  range.addEventListener("input", () => {
    const v = clamp(parseFloat(range.value) || 0);
    cell.setValue(v);
    onLive?.(v);
  });
  range.addEventListener("change", () =>
    onChange(clamp(parseFloat(range.value) || 0)),
  );

  const inputs = document.createElement("div");
  inputs.className = "field-row working-area-inputs";
  const grp = document.createElement("div");
  grp.className = "working-area-input";
  const cap = document.createElement("label");
  cap.textContent = "Start offset";
  grp.appendChild(cap);
  grp.appendChild(cell.row);
  inputs.appendChild(grp);

  wrap.appendChild(track);
  wrap.appendChild(inputs);
  return wrap;
}

export function numberField(
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  onChange: (v: number) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  wrap.appendChild(lbl);
  const row = document.createElement("div");
  row.className = "field-row";
  const range = document.createElement("input");
  range.type = "range";
  range.min = String(min);
  range.max = String(max);
  range.step = String(step);
  range.value = String(value);
  const num = document.createElement("input");
  num.type = "number";
  num.min = String(min);
  num.max = String(max);
  num.step = String(step);
  num.value = String(value);
  num.style.maxWidth = "70px";
  range.addEventListener("input", () => {
    num.value = range.value;
    onChange(parseFloat(range.value));
  });
  num.addEventListener("input", () => {
    range.value = num.value;
    onChange(parseFloat(num.value));
  });
  row.appendChild(range);
  row.appendChild(num);
  wrap.appendChild(row);
  return wrap;
}

export function checkbox(
  label: string,
  value: boolean,
  onChange: (v: boolean) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const row = document.createElement("label");
  row.style.display = "flex";
  row.style.gap = "6px";
  row.style.alignItems = "center";
  const cb = document.createElement("input");
  cb.type = "checkbox";
  cb.checked = value;
  cb.addEventListener("change", () => onChange(cb.checked));
  row.appendChild(cb);
  const span = document.createElement("span");
  span.textContent = label;
  row.appendChild(span);
  wrap.appendChild(row);
  return wrap;
}

export function select<T extends string>(
  label: string,
  options: [T, string][],
  value: T,
  onChange: (v: T) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "field";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  wrap.appendChild(lbl);
  const sel = document.createElement("select");
  for (const [v, name] of options) {
    const opt = document.createElement("option");
    opt.value = v;
    opt.textContent = name;
    if (v === value) opt.selected = true;
    sel.appendChild(opt);
  }
  sel.addEventListener("change", () => onChange(sel.value as T));
  wrap.appendChild(sel);
  return wrap;
}

/**
 * A searchable, scrollable list of standard object sizes (#32).
 *
 * A list rather than a `<select>` because the catalog is meant to grow: a
 * dropdown of forty entries is a scroll through someone else's ordering, while
 * a search box gets a maker to "coaster" or "101" in three keystrokes. Each row
 * shows the dimensions and carries its source as a tooltip, so a published
 * standard is distinguishable from a convention without opening anything.
 */
export function standardSizeList(
  sizes: readonly StandardSize[],
  selectedId: string | null,
  onPick: (size: StandardSize) => void,
  units: "mm" | "in",
): { row: HTMLElement; focusSearch: () => void } {
  const wrap = document.createElement("div");
  wrap.className = "field standard-size-field";

  const lbl = document.createElement("label");
  lbl.textContent = "Standard size";
  lbl.htmlFor = "standard-size-search";
  wrap.appendChild(lbl);

  const search = document.createElement("input");
  search.type = "search";
  search.id = "standard-size-search";
  search.className = "standard-size-search";
  search.placeholder = "Search sizes…";
  search.dataset.role = "standard-size-search";
  search.setAttribute("aria-label", "Search standard sizes");
  wrap.appendChild(search);

  const list = document.createElement("div");
  list.className = "standard-size-list";
  list.dataset.role = "standard-size-list";
  list.setAttribute("role", "listbox");
  wrap.appendChild(list);

  const empty = document.createElement("p");
  empty.className = "field-note standard-size-empty";
  empty.dataset.role = "standard-size-empty";
  empty.textContent = "Nothing matches that.";
  empty.hidden = true;
  wrap.appendChild(empty);

  const fmt = (mm: number) =>
    units === "in" ? `${(mm / 25.4).toFixed(2)} in` : `${mm.toFixed(1)} mm`;

  const render = () => {
    const matches = searchStandardSizes(sizes, search.value);
    list.innerHTML = "";
    empty.hidden = matches.length > 0;
    let group = "";
    for (const size of matches) {
      if (size.group !== group) {
        group = size.group;
        const head = document.createElement("div");
        head.className = "standard-size-group";
        head.textContent = group;
        list.appendChild(head);
      }
      const row = document.createElement("button");
      row.type = "button";
      row.className = "standard-size-row";
      row.dataset.sizeId = size.id;
      row.setAttribute("role", "option");
      const selected = size.id === selectedId;
      row.setAttribute("aria-selected", selected ? "true" : "false");
      if (selected) row.classList.add("selected");
      // The source travels with the value, so a maker can tell a standard from
      // a convention before cutting to it.
      row.title = size.hint;
      const name = document.createElement("span");
      name.className = "standard-size-name";
      name.textContent = size.label;
      const dims = document.createElement("span");
      dims.className = "standard-size-dims";
      dims.textContent = size.round
        ? `⌀ ${fmt(size.widthMm)}`
        : `${fmt(size.widthMm)} × ${fmt(size.heightMm)}`;
      row.appendChild(name);
      row.appendChild(dims);
      row.addEventListener("click", () => onPick(size));
      list.appendChild(row);
    }
  };
  search.addEventListener("input", render);
  render();

  return { row: wrap, focusSearch: () => search.focus() };
}
